import { OrgUserListItem } from './org-user.model';

/** Cognito pool password policy — keep in sync with `afas-serverless/cloudformation/cognito.yaml`. */
export const TEMP_PASSWORD_REQUIREMENT_ITEMS: readonly string[] = [
  'At least 8 characters',
  'At least one uppercase letter (A–Z)',
  'At least one lowercase letter (a–z)',
  'At least one number (0–9)',
  'At least one symbol (e.g. ! @ # $)',
];

export function usersHeader(count: number, loading: boolean): string {
  if (loading) {
    return 'Loading...';
  }
  return count === 1 ? '1 User' : `${count} Users`;
}

/** List row title — `name` when API provides it (B0), otherwise email. */
export function displayNameForList(user: Pick<OrgUserListItem, 'name' | 'email'>): string {
  const name = user.name?.trim();
  return name ? name : user.email;
}

/** Show email on line 2 when a display name is present (avoid duplicating email as both lines). */
export function showEmailSubtitle(user: Pick<OrgUserListItem, 'name' | 'email'>): boolean {
  return Boolean(user.name?.trim());
}

export type AccountStatusBadgeVariant = 'active' | 'disabled' | 'pending' | 'neutral';

/** CSS variant for the inline status badge on the user list row. */
export function accountStatusBadgeVariant(
  user: Pick<OrgUserListItem, 'enabled' | 'status'>,
): AccountStatusBadgeVariant {
  if (!user.enabled) {
    return 'disabled';
  }

  switch (user.status) {
    case 'CONFIRMED':
      return 'active';
    case 'FORCE_CHANGE_PASSWORD':
      return 'pending';
    default:
      return 'neutral';
  }
}

/** Maps Cognito list fields to a short account status label for the list badge. */
export function accountStatusLabel(user: Pick<OrgUserListItem, 'enabled' | 'status'>): string {
  if (!user.enabled) {
    return 'Disabled';
  }

  switch (user.status) {
    case 'FORCE_CHANGE_PASSWORD':
      return 'Pending password change';
    case 'CONFIRMED':
      return 'Active';
    case 'RESET_REQUIRED':
      return 'Password reset required';
    case 'UNCONFIRMED':
      return 'Unconfirmed';
    default:
      if (!user.status) {
        return 'Unknown';
      }
      return user.status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }
}
